import * as React from "react";
import {
  Card,
  Text,
  Badge,
  makeStyles,
  tokens,
  Caption1,
  Body1,
  Tooltip,
} from "@fluentui/react-components";
import {
  Chat24Regular,
  ErrorCircle24Regular,
  ArrowSyncCircle24Regular,
  CheckmarkCircle24Regular,
  Link24Regular,
} from "@fluentui/react-icons";
import { DiscussionDto } from "../../../services/discussions.service";
import { findTypeById } from "../types/discussionTypes";
import RichTextDisplay from "./RichTextDisplay";
import { normalizeStatus, getStatusIconColor } from "../../../utils/discussionStatusHelpers";

const useStyles = makeStyles({
  card: {
    marginBottom: tokens.spacingVerticalS,
    cursor: "pointer",
    transition: "all 0.2s ease",
    ":hover": {
      boxShadow: tokens.shadow8,
    },
  },
  cardContent: {
    padding: tokens.spacingVerticalS,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  typeIcon: {
    fontSize: tokens.fontSizeBase500,
    flexShrink: 0,
  },
  title: {
    flex: 1,
    fontWeight: tokens.fontWeightSemibold,
  },
  description: {
    color: tokens.colorNeutralForeground2,
    display: "-webkit-box",
    WebkitLineClamp: "1",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    lineHeight: "1.4",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    flexWrap: "wrap",
  },
  footerItem: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground3,
  },
  commentCount: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXXS,
  },
  statusIcon: {
    fontSize: tokens.fontSizeBase500,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
  },
});

interface DiscussionCardProps {
  discussion: DiscussionDto;
  onClick: (discussion: DiscussionDto) => void;
}

/**
 * Accent colour for the card border. Always sourced from the main discussion
 * type — tags get their own pills below, so they don't influence the accent.
 */
const getMainTypeColor = (mainType: string): string => {
  return findTypeById(mainType)?.color ?? "#0078D4";
};

/**
 * Get status display information (label, color, and icon)
 */
const getStatusInfo = (status?: string): { label: string; color: string; icon: React.ComponentType } => {
  const normalizedStatus = normalizeStatus(status);
  const color = getStatusIconColor(status);

  if (normalizedStatus.toLowerCase().includes("resolved") || normalizedStatus.toLowerCase().includes("closed")) {
    return { label: normalizedStatus, color, icon: CheckmarkCircle24Regular };
  } else if (normalizedStatus.toLowerCase().includes("completed")) {
    return { label: normalizedStatus, color, icon: CheckmarkCircle24Regular };
  } else if (normalizedStatus.toLowerCase().includes("reviewed")) {
    return { label: normalizedStatus, color, icon: CheckmarkCircle24Regular };
  } else if (normalizedStatus.toLowerCase().includes("open") || normalizedStatus.toLowerCase().includes("none")) {
    return { label: normalizedStatus, color, icon: ErrorCircle24Regular };
  } else {
    return { label: normalizedStatus, color, icon: ArrowSyncCircle24Regular };
  }
};

// Helper to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 60) {
    return diffMins === 1 ? "1 minute ago" : `${diffMins} minutes ago`;
  } else if (diffHours < 24) {
    return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
  } else if (diffDays < 7) {
    return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString();
  }
};

/**
 * List-row summary of a discussion — title, type/tag colouring, status,
 * author, comment count, and last-activity timestamp.
 */
const DiscussionCard: React.FC<DiscussionCardProps> = ({ discussion, onClick }) => {
  const styles = useStyles();
  const accentColor = getMainTypeColor(discussion.type);
  const statusInfo = getStatusInfo(discussion.status);

  const mainTypeDetails = findTypeById(discussion.type);
  const mainTypeName = mainTypeDetails?.name ?? discussion.type;

  const handleClick = () => {
    onClick(discussion);
  };

  return (
    <Card
      className={styles.card}
      onClick={handleClick}
      style={{
        borderLeft: `4px solid ${accentColor}`,
        borderTop: `1px solid ${accentColor}20`,
        borderRight: `1px solid ${accentColor}20`,
        borderBottom: `1px solid ${accentColor}20`,
      }}
    >
      <div className={styles.cardContent}>
        <div className={styles.cardHeader}>
          {mainTypeDetails && (
            <div
              className={styles.typeIcon}
              style={{ color: accentColor }}
            >
              {React.createElement(mainTypeDetails.icon)}
            </div>
          )}
          <Text className={styles.title}>{discussion.title}</Text>
          <Badge
            appearance="filled"
            style={{ backgroundColor: accentColor, color: "white" }}
          >
            {mainTypeName}
          </Badge>
          {discussion.tags?.map((tagId) => {
            const tagDetails = findTypeById(tagId);
            if (!tagDetails) return null;
            return (
              <Badge
                key={tagId}
                appearance="filled"
                style={{ backgroundColor: tagDetails.color, color: "white" }}
              >
                {tagDetails.name}
              </Badge>
            );
          })}
          <Tooltip content={statusInfo.label} relationship="description">
            <div
              className={styles.statusIcon}
              style={{ color: statusInfo.color }}
            >
              {React.createElement(statusInfo.icon)}
            </div>
          </Tooltip>
        </div>
        {(discussion.descriptionContent || discussion.description) && (
          <Body1 className={styles.description}>
            {discussion.descriptionContent ? (
              <RichTextDisplay markup={discussion.descriptionContent} />
            ) : (
              discussion.description
            )}
          </Body1>
        )}
        <div className={styles.footer}>
          <Caption1 className={styles.footerItem}>
            {discussion.createdByName}
          </Caption1>
          <Caption1 className={styles.footerItem}>
            {formatDate(discussion.createdAt)}
          </Caption1>
          <Caption1 className={styles.commentCount}>
            <Chat24Regular fontSize={16} />
            <span>{discussion.commentCount}</span>
          </Caption1>
          {discussion.type === "review_point" && discussion.statusItemIds && discussion.statusItemIds.length > 0 && (
            <Caption1 className={styles.footerItem}>
              <Link24Regular fontSize={16} />
              <span>{discussion.statusItemIds.length} item{discussion.statusItemIds.length > 1 ? "s" : ""}</span>
            </Caption1>
          )}
        </div>
      </div>
    </Card>
  );
};

export default DiscussionCard;
